import ImageOptimizer from '@/components/image-optimizer';
import {
  buildRecommendedStoreHref,
  getRecommendedStoreDisplayName,
  getRegionShortLabel,
} from '@/lib/recommended-store';
import { StoreEntity, StoreType } from '@/services/types/store.type';
import Link from 'next/link';

interface Props {
  store: StoreEntity;
  /** 추천 기준 지역 문자열("시도 시군구 읍면동") */
  region: string;
}

/** 4.27 → '약 4.3km' */
function formatDistance(distance: number) {
  return distance < 1 ? '1km 이내' : `약 ${distance.toFixed(1)}km`;
}

const ArrowIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 학원 리스트 검색창 아래 "학원 가기 전 연습하기 좋은 실내운전연습장" 추천 영역.
// 운전연수 사이트(web.dztraining)의 추천 배너와 같은 틀에 면허취득 컨셉 문구를 싣는다.
// 영역 전체가 지점 상세로 가는 하나의 링크이며, 상세에서는 추천 요금제를 함께 노출한다.
// PC 높이·매장 사진은 아래 학원 카드(academy-card)와 같게 둔다(212px·사진 162px). 모바일은 사진 78px 의
// 상하 여백을 좌우(테두리 1px + 16px)와 같게 두어 112px 이다. 높이를 바꾸면 광고 배너·조회 중 빈 자리도 함께 맞춘다.
// 매장명(h3)이 학원 카드보다 먼저 오도록 한 벌의 마크업을 반응형으로 배치한다
// (PC/모바일 마크업을 따로 두면 매장명이 DOM에 두 번 들어간다).
export default function RecommendedStoreBanner({ store, region }: Props) {
  const href = buildRecommendedStoreHref(store);
  const displayName = getRecommendedStoreDisplayName(store);
  const regionLabel = getRegionShortLabel(region);
  const isDirect = store.type === StoreType.DIRECT;
  const distanceLabel =
    typeof store.distance === 'number'
      ? `${regionLabel}에서 ${formatDistance(store.distance)}`
      : `${regionLabel} 근처`;
  const address = store.summaryAddress || store.address;

  return (
    <div className="desktop:flex desktop:justify-center">
      <aside
        aria-label="학원 가기 전 추천 실내운전연습장"
        className="desktop:web-w desktop:mt-8 desktop:w-full desktop:rounded-2xl mt-4 overflow-hidden rounded-xl"
      >
        <Link
          href={href}
          className="group desktop:h-[212px] desktop:gap-8 desktop:rounded-2xl desktop:pr-6 desktop:pl-12 hover:border-main-blue/40 relative flex h-[112px] w-full cursor-pointer items-center gap-3 overflow-hidden rounded-xl border border-[#d9e7fb] bg-[#eef5ff] px-4 transition hover:shadow-[0_6px_20px_rgba(20,116,250,0.14)]"
        >
          {/* 배경: 왼쪽 흰빛 → 하늘색, 오른쪽 옅은 파란 광원 */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-3/5 bg-[radial-gradient(ellipse_at_0%_50%,rgba(255,255,255,0.95),transparent_65%)]" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_82%_50%,rgba(20,116,250,0.14),transparent_55%)]" />

          {/* PC 좌측 카피 */}
          <div className="desktop:flex relative hidden min-w-0 flex-1 flex-col">
            <p className="flex items-center gap-2.5">
              <span className="bg-main-blue typo-22 rounded-md px-3 py-1.5 font-black text-white shadow-[0_4px_10px_rgba(20,116,250,0.3)]">
                학원 가기 전
              </span>
              <span className="typo-22 text-main-blue font-extrabold">
                감 잡고 학원 등록하세요
              </span>
            </p>
            <p className="typo-32 desktop_screen:typo-36 text-level-1 mt-3 font-extrabold whitespace-nowrap">
              실내운전연습장에서 미리 연습해 보세요
            </p>
            <p className="typo-20 text-level-4 mt-3 font-semibold">
              학원에서 부족한 연습, 시뮬레이터로 채우세요
            </p>
          </div>

          {/* 매장 카드 (모바일은 배너 전체, PC는 우측 흰 카드) */}
          <div className="desktop:h-[162px] desktop:w-[520px] desktop:flex-none desktop:gap-5 desktop:overflow-hidden desktop:rounded-lg desktop:bg-white desktop:pr-5 desktop:shadow-[0_4px_16px_rgba(18,25,44,0.08)] relative flex min-w-0 flex-1 items-center gap-3">
            <div className="desktop:size-[162px] desktop:rounded-none relative size-[78px] shrink-0 overflow-hidden rounded-[6px]">
              <ImageOptimizer
                src={store.photoPath ?? ''}
                alt={displayName + ' 매장 사진'}
                fill
                sizes="(min-width: 1180px) 162px, 78px"
                className="object-cover transition-transform duration-300 group-hover:scale-105"
              />
              {isDirect && (
                <span className="bg-main-yellow text-level-1 typo-13 desktop:inline-block absolute top-2 left-2 hidden rounded-sm px-1.5 py-1 leading-none font-bold">
                  직영점
                </span>
              )}
            </div>

            <div className="flex min-w-0 flex-1 flex-col justify-center">
              {/* 360px 폭에서도 한 줄에 들어가도록 375px 미만에서만 뱃지·문구를 한 단계 줄인다 */}
              <p className="desktop:hidden flex min-w-0 flex-wrap items-center gap-x-1 gap-y-1">
                <span className="bg-main-blue shrink-0 rounded-sm px-1.5 py-1 text-[13px] leading-none font-black text-white max-[374px]:px-1 max-[374px]:text-[12px]">
                  학원 가기 전
                </span>
                <span className="typo-15 text-main-blue max-[374px]:typo-14 truncate leading-tight font-bold">
                  실내운전연습장
                </span>
              </p>
              <div className="desktop:mt-0 mt-1 flex min-w-0 items-center gap-1.5">
                <h3 className="typo-16 desktop:typo-22 text-level-1 truncate leading-tight font-extrabold">
                  {displayName}
                </h3>
                {isDirect && (
                  <span className="bg-main-yellow text-level-1 desktop:hidden shrink-0 rounded-sm px-1.5 py-0.5 text-[11px] leading-none font-bold">
                    직영점
                  </span>
                )}
              </div>
              <p className="typo-16 desktop:text-main-blue text-level-4 desktop:mt-1.5 mt-0.5 truncate leading-tight font-semibold">
                {distanceLabel}
                {address && (
                  <span className="desktop:inline desktop:text-level-4 desktop:typo-14 hidden">
                    {' · '}
                    {address}
                  </span>
                )}
              </p>

              {/* PC 클릭 유도 버튼: 흔들리는 화살표 + 지나가는 빛 */}
              <span className="bg-main-blue desktop:inline-flex relative mt-4 hidden h-10 w-fit items-center gap-2.5 overflow-hidden rounded-full pr-1.5 pl-5 shadow-[0_6px_16px_rgba(20,116,250,0.35)] transition group-hover:bg-[#0f63d9]">
                <span className="typo-18 font-extrabold text-white">
                  매장 보러가기
                </span>
                <span className="text-main-blue flex size-7 items-center justify-center rounded-full bg-white">
                  <span className="animate-nudge-x flex">
                    <ArrowIcon size={14} />
                  </span>
                </span>
                <span className="animate-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/45 to-transparent" />
              </span>
            </div>

            {/* 모바일 이동 표시 */}
            <span
              aria-hidden
              className="desktop:hidden bg-main-blue flex size-7 shrink-0 items-center justify-center rounded-full text-white shadow-[0_4px_10px_rgba(20,116,250,0.35)]"
            >
              <span className="animate-nudge-x flex">
                <ArrowIcon size={14} />
              </span>
            </span>
          </div>
        </Link>
      </aside>
    </div>
  );
}
